/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { agentPromptTemplates } from '../../../agent/promptTemplates';

export interface AgentQuickStartContext {
  configName?: string;
  runName?: string | null;
  service?: string;
}

export interface AgentQuickAction {
  key: string;
  /** Chip text. Personalized when the home page queries have resolved. */
  label: (context: AgentQuickStartContext) => string;
  /** Prefilled (never auto-sent) chat draft. */
  prompt: (context: AgentQuickStartContext) => string;
}

/**
 * Library of openers, one per capability the engineer agent actually has — see
 * agentPromptTemplates for the tool or skill each one maps to. The tile shows a
 * random few per page load so returning users keep discovering what the agent
 * can do instead of learning to ignore the same two chips.
 *
 * Nothing speculative belongs here: a chip that promises something the backend
 * has no tool for turns a usage driver into a disappointment.
 */
export const agentQuickActions: readonly AgentQuickAction[] = [
  {
    key: 'vet-pvsim-config',
    label: ({ configName }) =>
      configName ? `Sanity-check ${configName}` : 'Sanity-check a config',
    prompt: ({ configName }) => agentPromptTemplates.sanityCheckConfig(configName),
  },
  {
    key: 'summarize-run',
    label: ({ runName }) => (runName ? `Summarize ${runName}` : 'Summarize my latest run'),
    prompt: ({ service, runName }) => agentPromptTemplates.summarizeRun(service, runName),
  },
  {
    key: 'diagnose-low-pr',
    label: () => 'Why is my PR low?',
    prompt: agentPromptTemplates.diagnoseLowPr,
  },
  {
    key: 'loss-waterfall',
    label: () => 'Show my loss waterfall',
    prompt: ({ runName }) => agentPromptTemplates.lossWaterfall(runName),
  },
  {
    key: 'compare-runs',
    label: () => 'Compare two of my runs',
    prompt: agentPromptTemplates.compareRuns,
  },
  {
    key: 'rerun-with-tweak',
    label: () => 'Rerun my last scenario',
    prompt: ({ runName }) => agentPromptTemplates.rerunWithTweak(runName),
  },
  {
    key: 'find-outlier',
    label: () => 'Find my outlier run',
    prompt: agentPromptTemplates.findOutlier,
  },
  {
    key: 'parametric-sweep',
    label: () => 'Sweep a parameter for me',
    prompt: agentPromptTemplates.parametricSweep,
  },
  {
    key: 'weather-comparison',
    label: () => 'Try a nearby weather file',
    prompt: agentPromptTemplates.weatherComparison,
  },
  {
    key: 'long-term-tmy',
    label: () => 'Run a 25-year study',
    prompt: agentPromptTemplates.longTermTmy,
  },
  {
    key: 'module-bake-off',
    label: () => 'Bake off two PV modules',
    prompt: agentPromptTemplates.moduleBakeOff,
  },
  {
    key: 'inverter-swap',
    label: () => 'Swap in another inverter',
    prompt: agentPromptTemplates.inverterSwap,
  },
  {
    key: 'inverter-details',
    label: () => 'Check an inverter spec',
    prompt: agentPromptTemplates.inverterDetails,
  },
  {
    key: 'dc-ac-ratio',
    label: () => 'Test a higher DC/AC ratio',
    prompt: agentPromptTemplates.dcAcRatioScenario,
  },
  {
    key: 'gcr-cost-tradeoff',
    label: () => 'Trade GCR against cost',
    prompt: agentPromptTemplates.gcrCostTradeoff,
  },
  {
    key: 'vet-pvcosting-config',
    label: () => 'Sanity-check my cost inputs',
    prompt: agentPromptTemplates.vetCostingConfig,
  },
  {
    key: 'vet-esscosting-config',
    label: () => 'Sanity-check my ESS inputs',
    prompt: agentPromptTemplates.vetEssCostingConfig,
  },
  {
    key: 'capex-breakdown',
    label: () => 'Where does my capex go?',
    prompt: agentPromptTemplates.capexBreakdown,
  },
  {
    key: 'ess-capex-breakdown',
    label: () => 'Break down my battery capex',
    prompt: agentPromptTemplates.essCapexBreakdown,
  },
  {
    key: 'ess-storage-sensitivity',
    label: () => 'What does more storage cost?',
    prompt: agentPromptTemplates.essStorageSensitivity,
  },
  {
    key: 'rank-nba-scenarios',
    label: () => 'Rank my NBA scenarios',
    prompt: agentPromptTemplates.rankNbaScenarios,
  },
  {
    key: 'nba-risk-spread',
    label: () => 'Explain my NBA risk spread',
    prompt: agentPromptTemplates.nbaRiskSpread,
  },
  {
    key: 'explain-nba-scenario',
    label: () => 'Explain an NBA scenario',
    prompt: agentPromptTemplates.explainNbaScenario,
  },
  {
    key: 'lookup-project',
    label: () => 'Look up a project in bnCRM',
    prompt: agentPromptTemplates.lookupProject,
  },
  {
    key: 'land-terms',
    label: () => 'What are my land terms?',
    prompt: agentPromptTemplates.landTerms,
  },
  {
    key: 'land-parcels',
    label: () => 'Which parcels are buildable?',
    prompt: agentPromptTemplates.landParcels,
  },
  {
    key: 'results-table',
    label: () => 'Build me a results table',
    prompt: agentPromptTemplates.resultsTable,
  },
  {
    key: 'plot-monthly',
    label: () => 'Plot monthly energy',
    prompt: ({ runName }) => agentPromptTemplates.plotMonthly(runName),
  },
  {
    key: 'custom-analysis',
    label: () => 'Run some Python for me',
    prompt: agentPromptTemplates.customAnalysis,
  },
  {
    key: 'knowledge-base',
    label: () => 'How does PowerAlpha model this?',
    prompt: agentPromptTemplates.knowledgeBaseLookup,
  },
  {
    key: 'explain-concept',
    label: () => 'Explain an input to me',
    prompt: agentPromptTemplates.explainConcept,
  },
];

/**
 * Picks `count` distinct actions. `random` is injectable so tests stay
 * deterministic; the personalized config/run actions are weighted first when
 * the home page queries have resolved, because a chip naming the user's own
 * work converts better than a generic one.
 */
export function pickAgentQuickActions(
  count: number,
  context: AgentQuickStartContext = {},
  random: () => number = Math.random,
): AgentQuickAction[] {
  const pinned = agentQuickActions.filter(
    (action) =>
      (action.key === 'vet-pvsim-config' && context.configName) ||
      (action.key === 'summarize-run' && context.runName),
  );
  const pool = agentQuickActions.filter((action) => !pinned.includes(action));

  // Fisher-Yates on a copy: distinct picks, unbiased order.
  for (let index = pool.length - 1; index > 0; index -= 1) {
    const swapWith = Math.floor(random() * (index + 1));
    [pool[index], pool[swapWith]] = [pool[swapWith], pool[index]];
  }

  return [...pinned, ...pool].slice(0, count);
}
